import FormContainer from "@app/components/forms/container/FormContainer";
import { useFormWithSchema } from "@app/components/forms/hooks/useFormWithSchema";
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import { CommonSelectModal } from "@app/components/modals/CommonModal";
import { FindPasswordSchema } from "@app/pages/auth/FindPassword/findPasswordSchema";
import { Path } from "@app/route_path";
import { Button, useModal } from "@shortsworks/ui";
import {
  useSignIn,
  useCreateEmailCode,
  createEmailCode,
  verifyEmailCode,
  useResetPassword,
  setAuthToken,
  ErrorCode,
} from "@shortsworks/user-api";
import { ArrowLeft } from "lucide-react";
import { useState, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EmailCodeSchema } from "@app/pages/auth/FindPassword/emailCodeSchema";
import { ChangePasswordSchema } from "@app/pages/auth/FindPassword/changePasswordSchema";

// 링크 관련 상수 파일로 관리하기
export const SHORTSWORKS_CHANNEL_TALK = "https://shortsworks.channel.io";

export const FindPassword = () => {
  const { showModal } = useModal();
  const navigate = useNavigate();

  const resetPassword = useResetPassword();

  const [isSendEmailCode, setIsSendEmailCode] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const findPasswordForm = useFormWithSchema({
    formSchemas: FindPasswordSchema,
    mode: "onBlur",
  });

  const emailCodeForm = useFormWithSchema({
    formSchemas: EmailCodeSchema,
    mode: "onBlur",
  });

  const changePasswordForm = useFormWithSchema({
    formSchemas: ChangePasswordSchema,
    mode: "onChange",
  });

  // parameter를 요구하는 hook은 state와 함께 사용하는 방향으로 수정 필요
  // const createEmailCode = useCreateEmailCode();

  const handleGoPrevious = () => {
    if (isSendEmailCode || isVerified) {
      setIsSendEmailCode(false);
      return setIsVerified(false);
    }
    return navigate(Path.LOGIN);
  };

  const handleCreateEmailCode = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const buttonElement: HTMLButtonElement = event.target as HTMLButtonElement;
    buttonElement.disabled = true;

    const parsedForm = findPasswordForm.parseForm();
    if (!parsedForm.success) {
      buttonElement.disabled = false;
      return showModal(CommonErrorModal, {
        title: "입력하신 이메일이 옳바르지 않습니다.",
      });
    }

    const { email } = parsedForm.data;

    try {
      await createEmailCode({ email });
    } catch (error: any) {
      if (error.error.code === ErrorCode.NUMBER_1003) {
        buttonElement.disabled = false;
        return showModal(CommonErrorModal, {
          title: "이메일 인증 실패",
          description: "계정이 존재하지 않습니다.",
        });
      }
      buttonElement.disabled = false;
      return showModal(CommonErrorModal, {
        title: "인증 코드 전송이 실패했습니다.",
      });
    }
    buttonElement.disabled = false;
    return setIsSendEmailCode(true);
  };

  const handleVerifyEmailCode = async () => {
    const parsedFindPasswordForm = findPasswordForm.parseForm();
    const parsedEmailCodeForm = emailCodeForm.parseForm();
    if (!parsedFindPasswordForm.success || !parsedEmailCodeForm.success) {
      return;
    }

    const { email } = parsedFindPasswordForm.data;
    const { code } = parsedEmailCodeForm.data;

    try {
      await verifyEmailCode({ email, code });
      return setIsVerified(true);
    } catch (error) {
      return showModal(CommonErrorModal, {
        title: "인증 실패",
        description: "인증 코드가 옳바르지 않습니다."
      });
    }
  };

  const handleChangePassword = async () => {
    const parsedChangePasswordForm = changePasswordForm.parseForm();
    const parsedFindPasswordForm = findPasswordForm.parseForm();
    if (!parsedChangePasswordForm.success || !parsedFindPasswordForm.success) {
      return;
    }

    const { email } = parsedFindPasswordForm.data;
    const { password, passwordConfirm } = parsedChangePasswordForm.data;

    if (password !== passwordConfirm) {
      return showModal(CommonErrorModal, {
        title: "비밀번호와 비밀번호 확인이 일치하지 않습니다.",
      });
    }

    const resetPasswordResult = await resetPassword.mutateAsync({
      data: {
        email,
        password,
      },
    });

    // @ts-ignore
    const accessToken = resetPasswordResult.data.accessToken;
    // @ts-ignore
    const refreshToken = resetPasswordResult.data.refreshToken;

    if (!accessToken) {
      return showModal(CommonErrorModal, {
        title: "비밀번호 변경에 실패했습니다.",
      });
    }

    const commonSelectModalLoginResult = await showModal(CommonSelectModal, {
      title: "비밀번호가 변경되었습니다. 로그인하시겠습니까?",
    }).promise();
    if (commonSelectModalLoginResult.value) {
      setAuthToken({ accessToken, refreshToken });
      return navigate(Path.DASHBOARD);
    }
  };

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="mx-auto grid w-[350px] gap-6">
        <div className="grid gap-2">
          <div
            onClick={handleGoPrevious}
            className="flex flex-row align-middle gap-2"
          >
            <ArrowLeft className="cursor-pointer" height={36} />
            <h1 className="text-3xl font-bold">비밀번호 재설정</h1>
          </div>
          {!isVerified && (
            <>
              {!isSendEmailCode && (
                <>
                  <div className="grid gap-4 mt-4">
                    <Suspense>
                      <FormContainer
                        schemas={findPasswordForm.schemas}
                        form={findPasswordForm.form}
                      />
                    </Suspense>
                  </div>
                  <Button className="mt-4" onClick={handleCreateEmailCode}>
                    인증코드 전송
                  </Button>
                </>
              )}
              {isSendEmailCode && (
                <>
                  <div className="grid gap-4 mt-4">
                    <Suspense>
                      <FormContainer
                        schemas={emailCodeForm.schemas}
                        form={emailCodeForm.form}
                      />
                    </Suspense>
                  </div>
                  <Button className="mt-4" onClick={handleVerifyEmailCode}>
                    다음
                  </Button>
                </>
              )}
            </>
          )}
          {isVerified && (
            <>
              <div className="grid gap-4 mt-4">
                <Suspense>
                  <FormContainer
                    schemas={changePasswordForm.schemas}
                    form={changePasswordForm.form}
                  />
                </Suspense>
              </div>
              <Button className="mt-4" onClick={handleChangePassword}>
                변경하기
              </Button>
            </>
          )}
          <div className="text-sm flex flex-row justify-center gap-1">
            <div>다른 문의가 있으신가요?</div>
            <a
              href={SHORTSWORKS_CHANNEL_TALK}
              className="underline cursor-pointer"
            >
              문의하기
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
